<template>
  <AdminLayout page-title="Account Settings" page-subtitle="Manage your account security, notifications, and system preferences">
    <div class="space-y-8">
      <!-- Security Settings -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Security & Authentication</h3>
            <p class="text-slate-400 text-sm">Manage your password, two-factor authentication, and login security</p>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Password Management -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center space-x-4">
                <div class="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-semibold text-white">Password</h4>
                  <p class="text-sm text-slate-400">Last changed {{ securitySettings.passwordLastChanged }} days ago</p>
                </div>
              </div>
              <button @click="showChangePasswordModal = true" class="btn-oilbuddy-primary">
                Change Password
              </button>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span class="text-slate-300">Strong password</span>
              </div>
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span class="text-slate-300">Regular updates</span>
              </div>
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span class="text-slate-300">Encrypted storage</span>
              </div>
            </div>
          </div>

          <!-- Two-Factor Authentication -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center space-x-4">
                <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-semibold text-white">Two-Factor Authentication</h4>
                  <p class="text-sm text-slate-400">
                    {{ securitySettings.twoFactorEnabled ? 'Enabled - Your account is protected' : 'Not enabled - Add extra security' }}
                  </p>
                </div>
              </div>
              <button @click="toggleTwoFactor" class="btn-oilbuddy-secondary">
                {{ securitySettings.twoFactorEnabled ? 'Disable' : 'Enable' }}
              </button>
            </div>
            <div v-if="securitySettings.twoFactorEnabled" class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span class="text-slate-300">Authenticator app linked</span>
              </div>
              <div class="flex items-center space-x-2">
                <div class="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span class="text-slate-300">Backup codes generated</span>
              </div>
            </div>
          </div>

          <!-- Session Management -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center space-x-4">
                <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                  <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-semibold text-white">Active Sessions</h4>
                  <p class="text-sm text-slate-400">{{ securitySettings.activeSessions.length }} active session(s)</p>
                </div>
              </div>
              <button @click="showSessionsModal = true" class="btn-oilbuddy-secondary">
                Manage Sessions
              </button>
            </div>
            <div class="space-y-3">
              <div v-for="session in securitySettings.activeSessions.slice(0, 2)" :key="session.id" class="flex items-center justify-between p-3 bg-slate-600/30 rounded-lg">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-white">{{ session.device }}</p>
                    <p class="text-xs text-slate-400">{{ session.location }} • {{ session.lastActive }}</p>
                  </div>
                </div>
                <span v-if="session.current" class="text-xs text-emerald-400 font-medium">Current</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Notification Settings -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Notification Preferences</h3>
            <p class="text-slate-400 text-sm">Configure how and when you receive notifications</p>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Email Notifications -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Email Notifications</h4>
            <div class="space-y-4">
              <div v-for="notification in emailNotifications" :key="notification.id" class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">{{ notification.title }}</p>
                  <p class="text-sm text-slate-400">{{ notification.description }}</p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="notification.enabled"
                    type="checkbox"
                    class="sr-only peer"
                  >
                  <div class="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-400 peer-checked:to-cyan-400"></div>
                </label>
              </div>
            </div>
          </div>

          <!-- Push Notifications -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Push Notifications</h4>
            <div class="space-y-4">
              <div v-for="notification in pushNotifications" :key="notification.id" class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">{{ notification.title }}</p>
                  <p class="text-sm text-slate-400">{{ notification.description }}</p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="notification.enabled"
                    type="checkbox"
                    class="sr-only peer"
                  >
                  <div class="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-400 peer-checked:to-cyan-400"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Privacy Settings -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Privacy & Data</h3>
            <p class="text-slate-400 text-sm">Control your data privacy and account visibility</p>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Data Collection -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Data Collection</h4>
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Analytics Data</p>
                  <p class="text-sm text-slate-400">Allow collection of usage analytics to improve the platform</p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="privacySettings.analyticsEnabled"
                    type="checkbox"
                    class="sr-only peer"
                  >
                  <div class="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-400 peer-checked:to-cyan-400"></div>
                </label>
              </div>
              <div class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-white">Performance Monitoring</p>
                  <p class="text-sm text-slate-400">Help us identify and fix performance issues</p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="privacySettings.performanceMonitoring"
                    type="checkbox"
                    class="sr-only peer"
                  >
                  <div class="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-400 peer-checked:to-cyan-400"></div>
                </label>
              </div>
            </div>
          </div>

          <!-- Data Export & Deletion -->
          <div class="bg-slate-700/30 rounded-xl p-6">
            <h4 class="font-semibold text-white mb-4">Data Management</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button @click="exportData" class="flex items-center justify-center space-x-2 p-4 bg-slate-600/30 hover:bg-slate-600/50 rounded-xl transition-colors">
                <svg class="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span class="text-white font-medium">Export My Data</span>
              </button>
              <button @click="showDeleteAccountModal = true" class="flex items-center justify-center space-x-2 p-4 bg-red-500/10 hover:bg-red-500/20 rounded-xl transition-colors border border-red-500/20">
                <svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                <span class="text-red-400 font-medium">Delete Account</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- API & Integration Settings -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">API & Integrations</h3>
            <p class="text-slate-400 text-sm">Manage API keys and third-party integrations</p>
          </div>
          <button @click="showCreateApiKeyModal = true" class="btn-oilbuddy-primary">
            Create API Key
          </button>
        </div>

        <div class="space-y-4">
          <div v-for="apiKey in apiKeys" :key="apiKey.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
            <div class="flex items-center space-x-4">
              <div class="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
                </svg>
              </div>
              <div>
                <p class="font-medium text-white">{{ apiKey.name }}</p>
                <p class="text-sm text-slate-400">{{ apiKey.permissions }} • Created {{ apiKey.created }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="apiKey.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'">
                {{ apiKey.active ? 'Active' : 'Inactive' }}
              </span>
              <button @click="revokeApiKey(apiKey.id)" class="p-2 text-slate-400 hover:text-red-400 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Change Password Modal -->
    <div v-if="showChangePasswordModal" class="modal-overlay" @click="closeChangePasswordModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Change Password</h3>
          <button @click="closeChangePasswordModal" class="modal-close">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="changePassword" class="modal-body">
          <div class="form-group">
            <label class="form-label">Current Password</label>
            <input
              v-model="passwordForm.current"
              type="password"
              required
              class="form-input"
              placeholder="Enter your current password"
            >
          </div>
          <div class="form-group">
            <label class="form-label">New Password</label>
            <input
              v-model="passwordForm.new"
              type="password"
              required
              class="form-input"
              placeholder="Enter your new password"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Confirm New Password</label>
            <input
              v-model="passwordForm.confirm"
              type="password"
              required
              class="form-input"
              placeholder="Confirm your new password"
            >
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeChangePasswordModal" class="btn-secondary">
              Cancel
            </button>
            <button type="submit" class="btn-oilbuddy-primary">
              Update Password
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Account Modal -->
    <div v-if="showDeleteAccountModal" class="modal-overlay" @click="closeDeleteAccountModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title text-red-400">Delete Account</h3>
          <button @click="closeDeleteAccountModal" class="modal-close">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
            <div class="flex items-start space-x-3">
              <svg class="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
              <div>
                <h4 class="font-semibold text-red-400 mb-1">Warning: This action is irreversible</h4>
                <p class="text-sm text-slate-300">Deleting your account will permanently remove all your data, including profile information, settings, and activity history.</p>
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label">Type "DELETE" to confirm</label>
            <input
              v-model="deleteConfirmation"
              type="text"
              class="form-input"
              placeholder="DELETE"
            >
          </div>
          
          <div class="modal-actions">
            <button type="button" @click="closeDeleteAccountModal" class="btn-secondary">
              Cancel
            </button>
            <button
              @click="deleteAccount"
              :disabled="deleteConfirmation !== 'DELETE'"
              class="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import AdminLayout from './AdminLayout.vue'

interface SecuritySettings {
  passwordLastChanged: number
  twoFactorEnabled: boolean
  activeSessions: Array<{
    id: string
    device: string
    location: string
    lastActive: string
    current: boolean
  }>
}

interface NotificationSetting {
  id: string
  title: string
  description: string
  enabled: boolean
}

interface PrivacySettings {
  analyticsEnabled: boolean
  performanceMonitoring: boolean
}

interface ApiKey {
  id: string
  name: string
  permissions: string
  created: string
  active: boolean
}

// Reactive state
const showChangePasswordModal = ref(false)
const showSessionsModal = ref(false)
const showDeleteAccountModal = ref(false)
const showCreateApiKeyModal = ref(false)
const deleteConfirmation = ref('')

const passwordForm = ref({
  current: '',
  new: '',
  confirm: ''
})

const securitySettings = ref<SecuritySettings>({
  passwordLastChanged: 45,
  twoFactorEnabled: true,
  activeSessions: [
    {
      id: '1',
      device: 'Chrome on Windows',
      location: 'Houston, TX',
      lastActive: '2 minutes ago',
      current: true
    },
    {
      id: '2',
      device: 'Safari on iPhone',
      location: 'Dallas, TX',
      lastActive: '1 day ago',
      current: false
    },
    {
      id: '3',
      device: 'Firefox on Mac',
      location: 'Austin, TX',
      lastActive: '3 days ago',
      current: false
    }
  ]
})

const emailNotifications = ref<NotificationSetting[]>([
  {
    id: 'security',
    title: 'Security Alerts',
    description: 'Login attempts, password changes, and security events',
    enabled: true
  },
  {
    id: 'system',
    title: 'System Updates',
    description: 'Platform updates, maintenance notifications',
    enabled: true
  },
  {
    id: 'reports',
    title: 'Weekly Reports',
    description: 'Weekly activity and performance summaries',
    enabled: false
  },
  {
    id: 'marketing',
    title: 'Marketing Communications',
    description: 'Product updates, tips, and promotional content',
    enabled: false
  }
])

const pushNotifications = ref<NotificationSetting[]>([
  {
    id: 'urgent',
    title: 'Urgent Alerts',
    description: 'Critical system alerts and emergencies',
    enabled: true
  },
  {
    id: 'mentions',
    title: 'Mentions & Messages',
    description: 'When someone mentions you or sends a message',
    enabled: true
  },
  {
    id: 'updates',
    title: 'Activity Updates',
    description: 'Updates on projects and tasks you follow',
    enabled: false
  }
])

const privacySettings = ref<PrivacySettings>({
  analyticsEnabled: true,
  performanceMonitoring: true
})

const apiKeys = ref<ApiKey[]>([
  {
    id: '1',
    name: 'Production API',
    permissions: 'Read/Write',
    created: '2 months ago',
    active: true
  },
  {
    id: '2',
    name: 'Mobile App',
    permissions: 'Read Only',
    created: '1 month ago',
    active: true
  },
  {
    id: '3',
    name: 'Development',
    permissions: 'Read/Write',
    created: '2 weeks ago',
    active: false
  }
])

// Methods
const changePassword = () => {
  if (passwordForm.value.new !== passwordForm.value.confirm) {
    alert('New passwords do not match!')
    return
  }
  
  securitySettings.value.passwordLastChanged = 0
  closeChangePasswordModal()
  alert('Password changed successfully!')
}

const toggleTwoFactor = () => {
  securitySettings.value.twoFactorEnabled = !securitySettings.value.twoFactorEnabled
  const status = securitySettings.value.twoFactorEnabled ? 'enabled' : 'disabled'
  alert(`Two-factor authentication ${status} successfully!`)
}

const exportData = () => {
  alert('Your data export has been initiated. You will receive an email with download instructions within 24 hours.')
}

const deleteAccount = () => {
  if (deleteConfirmation.value !== 'DELETE') {
    alert('Please type "DELETE" to confirm account deletion.')
    return
  }
  
  alert('Account deletion initiated. You will receive a confirmation email.')
  closeDeleteAccountModal()
}

const revokeApiKey = (keyId: string) => {
  if (confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
    const keyIndex = apiKeys.value.findIndex(key => key.id === keyId)
    if (keyIndex !== -1) {
      apiKeys.value.splice(keyIndex, 1)
      alert('API key revoked successfully.')
    }
  }
}

// Modal management
const closeChangePasswordModal = () => {
  showChangePasswordModal.value = false
  passwordForm.value = { current: '', new: '', confirm: '' }
}

const closeDeleteAccountModal = () => {
  showDeleteAccountModal.value = false
  deleteConfirmation.value = ''
}
</script>

<style scoped>
.admin-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

/* Modal Styles */
.modal-overlay {
  @apply fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50;
}

.modal-content {
  @apply bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden;
}

.modal-header {
  @apply flex items-center justify-between p-6 border-b border-slate-700/50;
}

.modal-title {
  @apply text-lg font-medium text-white;
}

.modal-close {
  @apply text-slate-400 hover:text-slate-300 transition-colors;
}

.modal-body {
  @apply p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)];
}

.modal-actions {
  @apply flex items-center justify-end space-x-3 pt-4 border-t border-slate-700/50;
}

/* Form Styles */
.form-group {
  @apply mb-6;
}

.form-label {
  @apply block text-sm font-medium text-slate-300 mb-3;
}

.form-input {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200;
}

/* OilBuddy Button Styles */
.btn-oilbuddy-primary {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 font-semibold rounded-lg hover:from-emerald-500 hover:to-cyan-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.btn-oilbuddy-secondary {
  @apply inline-flex items-center px-4 py-2 bg-slate-700/50 text-slate-300 font-medium rounded-lg hover:bg-slate-600/50 border border-emerald-400/20 hover:border-emerald-400/40 transition-all duration-200;
}

.btn-secondary {
  @apply inline-flex items-center px-4 py-2 bg-slate-700/50 text-slate-300 font-medium rounded-lg hover:bg-slate-600/50 transition-colors duration-200;
}
</style>
